import Prof from "@didi/mfe-global-utils/lib/prof";
import { preserveQuery, useRouter } from "~/utils/router";
import { useStore, useState } from "./store";
import type { IRcextStoreState } from "@/store/modules/rcext";
import type { INativeStoreState } from "@/store/modules/native";
import {
  CANONICAL_COUNTRY_CODE,
  COUNTRY_CALLING_CODE,
  COUNTRY_ID,
} from "~/constants/common";
import { lang } from "./lang";
import Cookies from "js-cookie";
import { getCookie, getParentDomain } from "./cookie";
import { isMarket, isFromMarKeTing, isDfcWebsiteSource } from "./scene";
import { trackEvent } from "@/utils/register/omega";
import { querySet } from "./url";
import { authorizeGrantToken } from "@/api/apply";
import { setConfig } from "@didi/error-base";
import log from '@/utils/log'

const syncErrorBaseUserId = (uid?: string | number) => {
  setConfig({
    userId: uid ? String(uid) : "",
  });
};
/**
 * cookie过期时间 单位天
 */
const DEFAULT_EXPIRES = 2;

const isProdLikeEnv = () =>
  process.env.NODE_ENV === "production" || process.env.NODE_ENV === "preview";

/**
 * 计算 external_token 写入的 cookie domain
 * - 非生产/preview：返回空（不限定 domain，本地可读写）
 * - 官网来源（外投或非外投）：写父域（如 .didifinance.com），供渠道根域共享登录态
 * - 其余场景：维持现状，锁定当前子域
 */
const getCookieDomain = (): string => {
  if (!isProdLikeEnv()) return "";
  if (isDfcWebsiteSource()) return getParentDomain(location.hostname);
  // 非官网来源维持历史行为：用 location.host（含端口），锁定当前子域；勿与上面父域分支的 hostname 合并
  return location.host;
};

/**
 * 设置外部token的Cookie
 * @param token - 需要设置的token值
 */
export function setCookie(token: string) {
  // 官网来源会话全程写父域，避免子域/父域同名 external_token 互相遮蔽
  Cookies.set("external_token", token, {
    domain: getCookieDomain(),
    path: "/",
    expires: DEFAULT_EXPIRES,
  });
}

/**
 * passport国际化版本映射对象
 */
const loginLibs = {
  // 生产环境
  production:
    "//static.didiglobal.com/common-biz/global-login/2.2.6/login.global.min.js",
};

const PASSPORT_URL = loginLibs["production"];

/**
 * 将token传递给passport SDK
 * 用于在非营销场景下，将cookie中的token同步到passport SDK中
 * @param token - 需要设置的token值
 * @returns Promise<string> - 成功返回'success'，失败返回reject('error')
 */
export const setTicket = (token: string) => {
  return new Promise((resolve, reject) => {
    window.login?.setTicket(
      token,
      () => {
        resolve("success");
      },
      () => {
        reject("error");
      }
    );
  });
};

export const requireRemote = Prof.requireRemote;

/**
 * 登录处理函数：若用户未登录,跳转登录页；若用户已登录，执行回调，去往首页
 */
export const handleLogin = async () => {
  // 优先检测cookie中的token 过期时间2天
  const token = Cookies.get("external_token") || getCookie("ticket");
  if (!token) {
    useStore().commit("user/update", {
      uid: "",
      phone: "",
      maskedPhone: "",
      token: "",
      isSilentLogin: false,
    });
    useRouter()?.replace(
      preserveQuery({
        name: "login",
      })
    );
    return Promise.reject("not login");
  }
  // cookie中有token时 再调用passport检测
  const config = {};
  // 如果是外投场景 需要给passport提供额外参数
  if (isMarket()) {
    const mediaPassportInfo = window.adv.getAdvExtraInfoForPassport();
    Object.assign(config, mediaPassportInfo);
  }
  if (!window.login) {
    try {
      await initPassport(config);
      console.log("init passport success");
    } catch (err) {
      log.error("init passport error", { err });
      throw err
    }
  }
  return new Promise((resolve, reject) => {
    // 用户token过期触发强制登陆，isLogin为true时表示用户已登录
    window.login?.isLogin(
      (res: { login: boolean; ticket: string; cell: number; uid: number }) => {

        const { login, ticket, cell, uid } = res;
        if (login) {
          useStore().commit("user/update", {
            uid,
            phone: cell,
            maskedPhone: getMaskedPhone(cell),
            token: ticket,
            isSilentLogin: false,
          });
          syncErrorBaseUserId(uid);
          // 登录成功后更新omega公参uid 同时最外层也上报uid	
          window.Omega?.setConfig({	
            userId: uid,	
            attrs: {	
              uid: uid,	
            }	
          })
          setCookie(ticket)
          resolve("has logined");
        } else {
          //登陆过期，先清除store中的数据
          syncErrorBaseUserId();
          useStore().commit("user/update", {
              uid: "",
              phone: "",
              maskedPhone: "",
              token: "",
              isSilentLogin: false,
            });
            // 跳转到自定义登陆
            useRouter()?.replace(
              preserveQuery({
                name: "login",
                query:{
                  expired_source:'expired'
                }
              })
            );
            resolve("Go to login");
        }
      },
      (err: any) => {
         //登陆过期，先清除store中的数据
        syncErrorBaseUserId();
        useStore().commit("user/update", {
          uid: "",
          phone: "",
          maskedPhone: "",
          token: "",
          isSilentLogin: false,
        });
        // 跳转到自定义登陆
        useRouter()?.replace(
          preserveQuery({
            name: "login",
            query:{
              expired_source:'expired'
            }
          })
        );
        reject("passport sdk error");
      }
    );
  });
};

export const relogin = async() => {
  // 用户登录过期 重新唤起登录
  try {
    await handleLogin()
  } catch (err) {
    log.error('relogin error', { err })
  }
};

/**
 * 检测是否登录，没登录不做后续处理，已登录仅更新登录信息，也不做跳转
 */
export const checkLoginStatus = async () => {
    trackEvent('tech_credit_outside_sw', {
        event_name: 'tech_login_status_check_start'
    })

  // 优先检测cookie中的token 过期时间2天
  let token = Cookies.get("external_token") || getCookie("ticket");
  let isSilentLoginSuccess = false;
  const isFromMarketing = isFromMarKeTing();
  // console.log('token: ',token)
  const uidForMarket = Cookies.get("uid");
  const phoneForMarket = Cookies.get("cell");
  if (!token) {
    // ============ WhatsApp 静默登录逻辑 ============
    // 仅当本地无登录 token 时才尝试静默登录（避免已登录用户被静默登录覆盖）
    const grantToken = getGrantTokenFromUrl()
    if (grantToken) {
      // 是否可以静默登录 1-是 0-否
      trackEvent('fin_credit_abroad_h5_process_page_silent_login_bt', {
        is_silent_login: 1
      })
      // 获取地理位置用于风控
      await fetchGeolocation()

      const result = await handleWhatsAppSilentLogin(grantToken)

      if (result.success) {
        isSilentLoginSuccess = true;
        // 是否登录成功
        trackEvent('fin_credit_abroad_application_card_register_bt', {
          result: 'success'
        })
        // 静默登录成功后刷新 token，继续走通用登录态补全（setTicket + isLogin）
        token = Cookies.get("external_token") || getCookie("ticket")
      } else {
        trackEvent('fin_credit_abroad_application_card_register_bt', {
          result: 'fail'
        })
      }
    } else {
        trackEvent('fin_credit_abroad_h5_process_page_silent_login_bt', {
          is_silent_login: 0
        })
    }

    if (!token) {
      return Promise.reject("not login");
    }
  }

  if (!isFromMarketing) {
    // 如果不是从营销页面打开
    if (!window.login) {
      await initPassport();
    }
    await setTicket(token); // 设置ticket
  }

  return new Promise((resolve, reject) => {
    if (token && isFromMarketing) {
      useStore().commit("user/update", {
        uid: uidForMarket,
        phone: phoneForMarket,
        maskedPhone: getMaskedPhone(phoneForMarket),
        token: token,
        isSilentLogin: isSilentLoginSuccess,
      });
      syncErrorBaseUserId(uidForMarket);
      useStore().commit("user/update", {
        marketFreeLogin: true,
      });
        trackEvent('tech_credit_outside_sw', {
            event_name: 'tech_login_status_has_login'
        })
      resolve("has logined");
    }
    window.login?.isLogin(
      (res: { login: boolean; ticket: string; cell: number; uid: number }) => {
        const { login, ticket, cell, uid } = res;
        if (login) {
          useStore().commit("user/update", {
            uid,
            phone: cell,
            maskedPhone: getMaskedPhone(cell),
            token: ticket,
            isSilentLogin: isSilentLoginSuccess,
          });
          syncErrorBaseUserId(uid);
          if (getCookie("ticket") && isFromMarKeTing()) {
            // 外投免登 记录免登标识
            useStore().commit("user/update", {
              marketFreeLogin: true,
            });
          }
            trackEvent('tech_credit_outside_sw', {
                event_name: 'tech_login_status_has_login'
            })
          resolve("has logined");
        } else {
            syncErrorBaseUserId();
            trackEvent('tech_credit_outside_sw', {
                event_name: 'tech_login_status_not_login'
            })
          reject("not login");
        }
      },
      (err: any) => {
          syncErrorBaseUserId();
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_login_status_passport_error'
          })
        reject("passport sdk error");
      }
    );
  });
};

const defaultLoginConf = {
  appid: 200112,
  role: 1,
  country_calling_code: COUNTRY_CALLING_CODE,
  country_id: COUNTRY_ID,
  canonical_country_code: CANONICAL_COUNTRY_CODE,
};

/**
 * 初始化passport sdk
 * @param config passport-sdk初始化所需参数
 * @returns
 */
/**
 * passport 事件绑定：在 passport 触发手机号校验进入验证码页(render -> code)
 * 时上报校验成功；在 passport 抛出 errno=40001 错误时上报校验失败。
 * 用 window 上的全局 KEY 保证只绑定一次。
 */
const PASSPORT_EVENTS_BOUND_KEY = '__credit_outside_passport_login_events_bound__'

const bindPassportRender = () => {
    window.login.on('render', (event: any) => {
        if (event?.to === 'code') {
            trackEvent('fin_credit_abroad_sign_phone_passport_verif_bt', {
                result: 1,
                error_msg: '',
                error_code: ''
            })
        }
    })
}

const bindPassportError = () => {
    window.login.on('error', (event: any) => {
        const errCode = event?.error?.errno
        const errMsg = event?.error?.error
        if (errCode === 40001) {
            trackEvent('fin_credit_abroad_sign_phone_passport_verif_bt', {
                result: 0,
                error_msg: errMsg,
                error_code: errCode,
                trace_id: event?.error?.traceId,
            })
        }
    })
}

const bindPassportLoginEventsOnce = () => {
    if ((window as any)[PASSPORT_EVENTS_BOUND_KEY]) return
    if (!window.login || typeof window.login.on !== 'function') return
    ;(window as any)[PASSPORT_EVENTS_BOUND_KEY] = true
    bindPassportRender()
    bindPassportError()
}

export const initPassport = (config = {}) => {
  const marketConfig = {};
    trackEvent('tech_credit_outside_sw', {
        event_name: 'tech_passport_init_start'
    })
  // 如果是外投场景 需要给passport提供额外参数
  if (isMarket()) {
    const mediaPassportInfo = window.adv.getAdvExtraInfoForPassport();
    Object.assign(marketConfig, mediaPassportInfo);
  }
  const conf = {
    ...defaultLoginConf,
    lang,
    ...config,
    extra_info: {
      ...marketConfig,
    },
  };
  return new Promise((res, rej) => {
    const bootstrapPassport = async () => {
    if (window.login && window.login.setConfig) {
      try {
        window.login.setConfig(conf);
          bindPassportLoginEventsOnce();
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_passport_init_success'
          })
        res("success");
      } catch (err) {
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_passport_init_failed'
          })
        rej(err);
      }
    } else {
      const timeout = setTimeout(() => {
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_passport_init_timeout'
          })
        rej(new Error("passport sdk 加载超时"));
      }, 10000);

      requireRemote(
        {
          src: PASSPORT_URL,
          async: true,
        },
        () => {
          clearTimeout(timeout);
          if (window.login && window.login.setConfig) {
            try {
              window.login.setConfig(conf);
                bindPassportLoginEventsOnce();
                trackEvent('tech_credit_outside_sw', {
                    event_name: 'tech_passport_init_success'
                })
              res("success");
            } catch (err) {
                trackEvent('tech_credit_outside_sw', {
                    event_name: 'tech_passport_init_failed'
                })
              rej(err);
            }
          } else {
              trackEvent('tech_credit_outside_sw', {
                  event_name: 'tech_passport_init_failed'
              })
            rej(new Error("passport sdk 加载失败，window.login 不存在"));
          }
        }
      );
    }
    };

    bootstrapPassport().catch((error) => {
      trackEvent('tech_credit_outside_sw', {
          event_name: 'tech_passport_init_failed'
      })
      rej(error);
    });
  });
};

type TPassportSuccessReturn = {
  ticket: string;
  cell: string;
  uid: number;
  uid_str: string;
  suid: number;
};

type TPassportLoginError = {
  message: string
  code: string | number
}

const formatTrackErrorMsg = (msg: any): string => {
  if (typeof msg === 'string') {
    const raw = msg.trim()
    if (!raw) return ''
    try {
      const parsed = JSON.parse(raw)
      return formatTrackErrorMsg(parsed)
    } catch (e) {
      return raw
    }
  }
  if (msg === null || msg === undefined) return ''
  if (typeof msg === 'number' || typeof msg === 'boolean') {
    return String(msg)
  }
  if (typeof msg === 'object') {
    const preferred =
      msg.error ??
      msg.errorMsg ??
      msg.msg ??
      msg.message
    if (preferred !== undefined) {
      return formatTrackErrorMsg(preferred)
    }
    try {
      return JSON.stringify(msg)
    } catch (e) {
      return '[object Object]'
    }
  }
  return String(msg)
}

/**
 * 用户输入完手机号, 去登录（验证码/密码）
 */
export const userLogin = async (phone: string) => {
    trackEvent('tech_credit_outside_sw', {
        event_name: 'tech_user_login_start'
    })
  if (!window.login) {
    await initPassport();
  }

  return new Promise((resolve, reject) => {
    const handleSuccess = (res: TPassportSuccessReturn) => {
      // res增加校验 数据不可靠
      // 注：fin_credit_abroad_sign_phone_passport_verif_bt 已迁移至
      // passport 的 render('code') / error(errno=40001) 事件统一上报
      if (res.uid && res.ticket && res.cell) {
        useStore().commit("user/update", {
          uid: res.uid,
          token: res.ticket,
          phone: res.cell,
          maskedPhone: getMaskedPhone(res.cell),
          isSilentLogin: false,
        });
        syncErrorBaseUserId(res.uid);
        // 登录成功后更新omega公参uid 同时最外层也上报uid
        window.Omega?.setConfig({
          userId: res.uid,
          attrs: {
            uid: res.uid,
          },
        });
        setCookie(res.ticket);
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_user_login_success'
          })
        resolve(res);
      } else {
        const errorMsg = "已经登录但未获取到用户信息"
        console.error("已经登录但未获取到用户信息");
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_user_login_failed'
          })
        reject({
          message: errorMsg,
          code: '',
        } as TPassportLoginError);
      }
    };
    const handleError = (err?: any) => {
        const rawErrorMsg =
          err?.message ??
          err?.msg ??
          err?.errorMsg ??
          err?.response?.data?.errorMsg ??
          err?.response?.data?.message ??
          err
        const errorMsg = formatTrackErrorMsg(rawErrorMsg) || 'unknown_error'
        const rawErrorCode =
          err?.code ??
          err?.errorCode ??
          err?.response?.data?.errorCode ??
          err?.response?.data?.code
        const errorCode =
          rawErrorCode === null || rawErrorCode === undefined || rawErrorCode === ''
            ? ''
            : String(rawErrorCode)
        trackEvent('tech_credit_outside_sw', {
            event_name: 'tech_user_login_failed'
        })
      console.log("注册失败");
      reject({
        message: errorMsg || "登录失败",
        code: errorCode,
      } as TPassportLoginError);
    };
    window.login.checkPhone(phone, handleSuccess, handleError);
  });
};

/*
退出登录
*/

export const autoLogout = () => {
  return new Promise((resolve, reject) => {
    const logout = () => {
      window.login.logout(() => {
        resolve('success')
      }, () => {
        reject('fail')
      })
    }
    if (window.login && window.login.logout) {
      logout()
    } else {
      initPassport().then(() => {
        logout()
      }).catch(() => {
        reject('fail')
      })
    }
  })
}

export const LogInAgain = async () => {
  try {
    await autoLogout()
    await handleLogin()
    return {
      success: true,
      msg: 'Log in again'
    }
  } catch (err) {
    log.error('log_in_again', { err })
    throw err
  }
}

// ============ WhatsApp 静默登录相关函数 ============

const GEOLOCATION_TIMEOUT = 5000
const GEOLOCATION_PERMISSION_CACHE_KEY = 'silent_login_geolocation_permission'
const GEOLOCATION_PERMISSION_GRANTED = 'granted'
const GEOLOCATION_PERMISSION_DENIED = 'denied'

const getGeolocationPermissionCache = (): string => {
  try {
    return localStorage.getItem(GEOLOCATION_PERMISSION_CACHE_KEY) || ''
  } catch {
    return ''
  }
}

const setGeolocationPermissionCache = (state: string) => {
  try {
    localStorage.setItem(GEOLOCATION_PERMISSION_CACHE_KEY, state)
  } catch {
    // 忽略 localStorage 异常，避免阻塞静默登录
  }
}

/**
 * 获取用户地理位置并存入 rcext store
 * 失败或超时不阻塞流程，store 中 lng/lat 保持为空
 */
const fetchGeolocation = async (): Promise<void> => {
  if (!navigator.geolocation) {
    return
  }

  let currentPermission = getGeolocationPermissionCache()
  const permissions = (navigator as Navigator & {
    permissions?: {
      query: (descriptor: { name: PermissionName }) => Promise<{ state: PermissionState }>
    }
  }).permissions

  if (permissions?.query) {
    try {
      const status = await permissions.query({ name: 'geolocation' })
      if (status.state === GEOLOCATION_PERMISSION_GRANTED) {
        currentPermission = GEOLOCATION_PERMISSION_GRANTED
        setGeolocationPermissionCache(GEOLOCATION_PERMISSION_GRANTED)
      } else {
        if (status.state === GEOLOCATION_PERMISSION_DENIED) {
          currentPermission = GEOLOCATION_PERMISSION_DENIED
          setGeolocationPermissionCache(GEOLOCATION_PERMISSION_DENIED)
        }
        return
      }
    } catch {
      // 无法可靠判断权限状态时，直接跳过，避免弹窗或额外等待
      return
    }
  }

  // 不支持 Permissions API 时，仅对本地已知授权过的用户尝试定位，避免首次进入触发授权弹窗。
  if (!permissions?.query && currentPermission !== GEOLOCATION_PERMISSION_GRANTED) {
    return
  }

  if (currentPermission === GEOLOCATION_PERMISSION_DENIED) {
    return
  }

  return new Promise((resolve) => {
    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve()
    }

    const timer = setTimeout(() => {
      done()
    }, GEOLOCATION_TIMEOUT)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { longitude: lng, latitude: lat } = position.coords
        setGeolocationPermissionCache(GEOLOCATION_PERMISSION_GRANTED)
        useStore().commit('rcext/update', {
          lng: String(lng),
          lat: String(lat)
        })
        trackEvent('tech_credit_outside_sw', {
          event_name: 'tech_geolocation_success'
        })
        done()
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setGeolocationPermissionCache(GEOLOCATION_PERMISSION_DENIED)
        }
        trackEvent('tech_credit_outside_sw', {
          event_name: 'tech_geolocation_failed',
          error_code: error.code
        })
        done()
      },
      {
        enableHighAccuracy: false,
        timeout: GEOLOCATION_TIMEOUT,
        maximumAge: 60000
      }
    )
  })
}

/**
 * 从 URL 获取 grant_token 参数
 * @returns grant_token 或空字符串
 */
export const getGrantTokenFromUrl = (): string => {
  return querySet.grant_token || ''
}

/**
 * 获取 IMC 原始链接
 * 优先从 URL 参数获取，其次从 Referer 获取，最后用当前页面 URL 做兜底
 * @returns 原始链接
 */
export const getOriginUrl = (): string => {
  return querySet.originUrl || document.referrer || window.location.href
}

/**
 * 手机号掩码处理：取前3后4，中间3个掩码
 * @param phone 原始手机号
 * @returns 掩码后的手机号
 */
export const maskPhone = (phone: string): string => {
  if (!phone || phone.length < 7) return phone
  return phone.slice(0, 3) + '***' + phone.slice(-4)
}

const getMaskedPhone = (phone?: string | number): string => {
  if (phone === undefined || phone === null || phone === '') {
    return ''
  }
  return maskPhone(String(phone))
}

const handleMediaEvent = (mobile: string, uid: number) => {
  if(isMarket() && window.adv && window.adv.mediaEventUp) {
    window.adv.mediaEventUp(5001, {
        data: {
            callingCode: '52',
            mobile,
            uid,
        }
    })
  }
}

const syncSilentLoginProfile = async (ticket: string) => {
  try {
    if (!window.login) {
      await initPassport()
    }
    await setTicket(ticket)

    const profile = await new Promise<{
      login: boolean
      ticket: string
      cell: string | number
      uid: number
    }>((resolve, reject) => {
      window.login?.isLogin(
        (res: {
          login: boolean
          ticket: string
          cell: string | number
          uid: number
        }) => {
          if (res.login) {
            resolve(res)
            return
          }
          reject(new Error('silent login profile not ready'))
        },
        (err: any) => {
          reject(err)
        }
      )
    })

    const phone = String(profile.cell || '')
    useStore().commit('user/update', {
      uid: profile.uid,
      phone,
      maskedPhone: getMaskedPhone(phone),
      token: profile.ticket || ticket,
      silentLoginType: 'whatsapp',
      isSilentLogin: true,
      marketFreeLogin: true,
    })
    // 静默登录成功后媒体时间回传
    handleMediaEvent(phone, profile.uid)
    syncErrorBaseUserId(profile.uid)
    window.Omega?.setConfig({
      userId: profile.uid,
      attrs: {
        uid: profile.uid,
      },
    })
    return { success: true }
  } catch (error) {
    log.error('syncSilentLoginProfile', { error })
    return { success: false, error }
  }
}

/**
 * WhatsApp 静默登录处理
 * @param grantToken 授权令牌
 * @returns 登录结果
 */
export const handleWhatsAppSilentLogin = async (
  grantToken: string,
): Promise<{ success: boolean; data?: any; error?: any }> => {
  try {
    // 获取新增参数
    const originUrl = getOriginUrl()
    const { lng, lat } = useState<IRcextStoreState>('rcext', ['lng', 'lat'])
    const { bizSystemInfo: wsgenv } = useState<INativeStoreState>('native', ['bizSystemInfo'])

    const res = await authorizeGrantToken({
      grant_token: grantToken,
      country_calling_code: COUNTRY_CALLING_CODE,
      canonical_country_code: CANONICAL_COUNTRY_CODE,
      appid: 200112,
      role: 1,
      origin_id:  '1',
      origin_url: originUrl,
      ...(lng && lat ? { lng, lat, map_type: 'WGS84' } : {}),
      ...(wsgenv ? { wsgenv } : {})
    })

    if (res.errno === 0 && res.data?.ticket) {
      // 设置 token
      setCookie(res.data.ticket)

      // 更新 store
      // marketFreeLogin: true 用于触发平台协议注册和申卡流程
      // isSilentLogin: true 用于标识静默登录场景（UI 展示手机号提示等）
      useStore().commit('user/update', {
        token: res.data.ticket,
        silentLoginType: 'whatsapp',
        isSilentLogin: true,
        marketFreeLogin: true,
        maskedPhone: ''
      })

      await syncSilentLoginProfile(res.data.ticket)
      return { success: true, data: res.data }
    }
    return { success: false }
  } catch (error) {
    log.error('handleWhatsAppSilentLogin', { error })
    return { success: false, error }
  }
}
